import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Stack, useMediaQuery } from '@mui/material';
import { AutoIsoSelect } from '@/features/camera/components/AutoIsoSelect';
import GamePositionsRepresentation from '@/features/autoIso/components/GamePositionsRepresentation';
import AutoISOTeamSelector from '@/features/teams/components/AutoISOTeamSelector';
import { AutoIsoTeamSelectorProvider } from '@/features/teams/context/AutoIsoTeamSelectorContext';
import useServerStateContext from '@/shared/hooks/useServerStateContext';
import { AutoIsoObjectType } from '@/features/autoIso/types';
import PtzControls from './PtzControls';
import AutoDirectorControls from './AutoDirectorControls';
import { Group, Panel, PanelSize, usePanelRef } from 'react-resizable-panels';
import GripSeparator from '@/shared/components/GripSeparator';
import { useCurrentGame } from '@/features/games/context/CurrentGameContext';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTargetAiming } from '@/features/framingPresets/hooks/useTargetAiming';
import TargetModePanel from '@/features/framingPresets/components/TargetModePanel';
import TargetModeBar from '@/features/framingPresets/components/TargetModeBar';

// Widths (px) for the tools Panel. Expanded is the floor of the panel's max: the
// max grows past it to whatever width lets the surface fill its height (see
// toolsExpanded). Collapsed leaves just the Manual/Auto-ISO toggle. Between
// them, the surface scales to fit — until the panel drops below
// AUTOISO_RINK_HIDE_THRESHOLD, at which point it's too small to read and hides.
const AUTOISO_TOOLS_EXPANDED = 365;
// px 0.5 on each side of the tools Stack.
const AUTOISO_TOOLS_PADDING_X = 8;
const AUTOISO_TOOLS_COLLAPSED = 200;
const AUTOISO_RINK_HIDE_THRESHOLD = 260;

// Content width of the team selector / PTZ controls, and the Panel width it
// starts at (content + the 0.5 px on its Box). The Panel is deliberately NOT
// capped at that width. It used to be, and combined with the cap on the tools
// Panel that left the Group with no panel able to grow: every always-rendered
// Panel began at its maximum, so a drag's delta got clamped to zero headroom
// and the separators froze whenever the flag-gated auto-director Panel was
// hidden. The surplus lands as dead space to the right of the selector
// instead — the content is a fixed width and left-aligned, so the selector
// itself still never stretches.
const AUTOISO_TEAM_SELECTOR_WIDTH = 500;
const AUTOISO_TEAM_SELECTOR_PANEL = AUTOISO_TEAM_SELECTOR_WIDTH + 8;

const AutoIsoPanelContent: React.FC = () => {
  // Server State Context
  const { selectedObjectType } = useServerStateContext();
  const { game } = useCurrentGame();
  const { autoDirectorControls, zoomToggle } = useFlags();
  const trackingDisabled = game.enableTracking === false;

  // The tuning panel sits inside the feature rather than beside it: its own
  // Switch can turn auto-director on, so showing it on a game with tracking off
  // would hand out the feature the camera-row toggle is withholding.
  const showAutoDirectorControls = Boolean(autoDirectorControls && !trackingDisabled);

  const autoIsoMenuSx = { mb: 3 };

  const autoIsoSelectSx = { flex: 1 };

  const gamePositionsSx = { top: 5 };

  const teamSelectorSx = useMemo(
    () => ({ width: `${AUTOISO_TEAM_SELECTOR_WIDTH}px`, height: '100%' }),
    [],
  );

  // Detect mobile/tablet devices - use vertical layout for narrow screens or portrait orientation
  const isMobileOrPortrait = useMediaQuery('(max-width: 768px), (orientation: portrait)');

  // Track the tools Panel's live width so the surface can hide once the panel is
  // dragged too narrow to render it usefully. In the mobile/portrait layout the
  // Group is vertical, so `inPixels` is a height — the threshold doesn't apply.
  const toolsPanelRef = usePanelRef();
  const [toolsWidth, setToolsWidth] = useState(AUTOISO_TOOLS_EXPANDED);
  const handleToolsResize = useCallback((size: PanelSize) => setToolsWidth(size.inPixels), []);

  // Target mode (a Fixed Camera Clip aiming at a spot) needs the rink to
  // point with, even when tracking is off, and swaps the joystick for its own
  // section: only the regular zoom stays beside it.
  const { active: targetMode } = useTargetAiming();
  const rinkVisible =
    (!trackingDisabled || targetMode) &&
    (isMobileOrPortrait || toolsWidth >= AUTOISO_RINK_HIDE_THRESHOLD);

  // Width the tools Panel needs for the surface to take its full height. When the
  // panel's height changes, snap the panel to it; if the Group can't spare that
  // much, the surface falls back to fitting the width it gets.
  const [rinkFitWidth, setRinkFitWidth] = useState(0);
  const toolsExpanded = Math.max(
    AUTOISO_TOOLS_EXPANDED,
    rinkFitWidth + AUTOISO_TOOLS_PADDING_X,
  );

  // Entering target mode with the tools panel dragged too narrow for the rink
  // would leave nothing to click: open it up. Only on entering; later drags
  // are the user's.
  useEffect(() => {
    const panel = toolsPanelRef.current;
    if (!targetMode || isMobileOrPortrait || !panel) return;
    if (panel.getSize().inPixels < AUTOISO_RINK_HIDE_THRESHOLD) panel.resize(toolsExpanded);
  }, [targetMode]);

  useEffect(() => {
    if (isMobileOrPortrait || !rinkFitWidth) return;
    // Wait a frame: the new maxSize reaches the Group on its next render, and a
    // resize issued before that is clamped to the old max.
    const frame = requestAnimationFrame(() => toolsPanelRef.current?.resize(toolsExpanded));
    return () => cancelAnimationFrame(frame);
  }, [isMobileOrPortrait, rinkFitWidth, toolsExpanded, toolsPanelRef]);

  const orientation = isMobileOrPortrait ? 'vertical' : 'horizontal';
  const seperatorOrientation = isMobileOrPortrait ? 'horizontal' : 'vertical';

  return (
    <Box
      sx={{
        backgroundColor: '#1F1F25',
        width: '100%',
        height: '100%',
        minHeight: 0,
        borderRadius: 1,
        p: 1,
        overflow: 'hidden',
      }}
    >
      <Group
        key={orientation}
        orientation={orientation}
        style={{ height: '100%', minHeight: 0 }}
      >
        <Panel
          id="autoiso-tools"
          defaultSize={isMobileOrPortrait ? '35%' : `${AUTOISO_TOOLS_EXPANDED}px`}
          maxSize={isMobileOrPortrait ? '60%' : `${toolsExpanded}px`}
          minSize={isMobileOrPortrait ? '100px' : `${AUTOISO_TOOLS_COLLAPSED}px`}
          panelRef={toolsPanelRef}
          onResize={handleToolsResize}
          collapsible
        >
          <Stack direction="column" spacing={1.5} sx={{ px: 0.5, height: '100%' }}>
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', ...autoIsoMenuSx }}>
              {targetMode ? (
                <TargetModeBar sx={autoIsoSelectSx} />
              ) : (
                <AutoIsoSelect sx={autoIsoSelectSx} />
              )}
            </Stack>
            {rinkVisible && (
              <GamePositionsRepresentation
                sx={gamePositionsSx}
                onFitWidthChange={setRinkFitWidth}
              />
            )}
          </Stack>
        </Panel>
        <GripSeparator orientation={seperatorOrientation} />
        <Panel
          id="autoiso-team-selector"
          defaultSize={isMobileOrPortrait ? '35%' : `${AUTOISO_TEAM_SELECTOR_PANEL}px`}
          minSize={isMobileOrPortrait ? '96px' : '30px'}
        >
          <AutoIsoTeamSelectorProvider>
            <Box sx={{ px: 0.5, height: '100%' }}>
              {targetMode ? (
                <Stack direction="row" spacing={1} sx={teamSelectorSx}>
                  <TargetModePanel sx={{ flex: 1, minWidth: 0 }} />
                  {zoomToggle && (
                    <PtzControls zoomOnly sx={{ height: '100%', boxSizing: 'border-box' }} />
                  )}
                </Stack>
              ) : (
                <>
                  {(selectedObjectType === AutoIsoObjectType.Player ||
                    selectedObjectType === AutoIsoObjectType.BallOrPuck) && (
                    <AutoISOTeamSelector sx={teamSelectorSx} />
                  )}
                  {selectedObjectType === AutoIsoObjectType.None ? (
                    <PtzControls sx={teamSelectorSx} />
                  ) : null}
                </>
              )}
            </Box>
          </AutoIsoTeamSelectorProvider>
        </Panel>
        {showAutoDirectorControls && !targetMode && (
          <>
            <GripSeparator orientation={seperatorOrientation} />
            <Panel
              id="auto-director"
              defaultSize={isMobileOrPortrait ? '30%' : undefined}
              minSize={isMobileOrPortrait ? '96px' : '380px'}
            >
              <Box
                sx={{
                  px: 0.5,
                  height: '100%',
                  width: isMobileOrPortrait ? '100%' : '380px',
                  maxWidth: '100%',
                }}
              >
                <AutoDirectorControls />
              </Box>
            </Panel>
          </>
        )}
      </Group>
    </Box>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default React.memo(AutoIsoPanelContent);
